import { FC, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import theme from 'config/theme';
import Spacing from 'components/Spacing';
import Icon, { IconNames } from 'components/Icon';
import { useAppSelector } from 'helpers/hooks';
import { trackEvent } from 'helpers/mixpanelEvents';
import { sanitizeHtml } from 'helpers/utils';
import { getFaqsData } from 'selectors/faqs';
import { FaqsData } from 'store/faqs/types';
import classNames from 'classnames';

interface Props {
  title?: string;
  isNumbered?: boolean;
  isDarkBackground?: boolean;
}

export const FAQs: FC<Props> = ({ title, isNumbered, isDarkBackground }) => {
  const [itemVisible, setItemVisible] = useState<number>(0);
  const faqsData = useAppSelector(getFaqsData);

  const handleClick = (faq: FaqsData, i: number) => {
    const isExpanding = itemVisible !== i;
    if (isExpanding) {
      trackEvent('FAQ Expanded', {
        faq_id: faq.faq_id,
        question: faq.question,
      });
    }
    setItemVisible(prevItemVisible => (prevItemVisible === i ? -1 : i));
  };

  return (
    <div className="faqs-container">
      {title && <span className="h3-dtm">{title}</span>}
      <Spacing $size={20} />
      {faqsData?.map((item: FaqsData, i: number) => (
        <div className="faqs-accordion" key={i}>
          <div className="faqs-accordion-content" onClick={() => handleClick(item, i)}>
            {isNumbered ? (
              <div className="row">
                <div className={classNames('circle-number', { selected: itemVisible === i })}>
                  <span className="body-text color-white">{i + 1}</span>
                </div>
                <Spacing $isVertical $size={8} />
                <span
                  className={classNames('h4-text', {
                    'color-inkBlue': itemVisible !== i,
                    'color-oathBlue': itemVisible === i,
                  })}
                >
                  {item.question}
                </span>
              </div>
            ) : (
              <span className="h4-text">{item.question}</span>
            )}
            <div className="icon-container">
              <Icon
                name={itemVisible === i ? IconNames.Minus : IconNames.Plus}
                size={20}
                color={
                  itemVisible === i && isNumbered
                    ? theme.colors.oathBlue
                    : isDarkBackground
                      ? theme.colors.white
                      : theme.colors.inkBlue
                }
              />
            </div>
          </div>
          <AnimatePresence>
            {itemVisible === i && (
              <motion.div
                className="faqs-accordion-answer"
                initial={{ opacity: 0, y: '-100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              >
                <span
                  className="body-text"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.answer) }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};
